/**
 * Centralized Global State Store using use-s
 *
 * This file contains all global state configurations for the application.
 * Each state object has a unique key for global sharing across components.
 */

export const store = {
  // User-related state
  user: {
    value: null,
    key: 'global-user',
  },

  userData: {
    value: null,
    key: 'global-user-data',
  },

  // Bank-related state
  banks: {
    value: [],
    key: 'global-banks',
  },

  selectedBank: {
    value: null,
    key: 'global-selected-bank',
  },

  // Filter and search state
  searchValue: {
    value: '',
    key: 'global-search-value',
  },

  selectedDifficulty: {
    value: null,
    key: 'global-selected-difficulty',
  },

  selectedState: {
    value: null,
    key: 'global-selected-state',
  },

  filterByHomeState: {
    value: true,
    key: 'global-filter-by-home-state',
  },

  showBookmarked: {
    value: false,
    key: 'global-show-bookmarked',
  },

  showArchived: {
    value: false,
    key: 'global-show-archived',
  },

  // Bookmarked and archived banks
  bookmarkedBanks: {
    value: [],
    key: 'global-bookmarked-banks',
  },

  archivedBanks: {
    value: [],
    key: 'global-archived-banks',
  },

  hiddenBanks: {
    value: [],
    key: 'global-hidden-banks',
  },

  // Bonus tracking
  completedBankIds: {
    value: [],
    key: 'global-completed-bank-ids',
  },

  inProgressBankIds: {
    value: [],
    key: 'global-in-progress-bank-ids',
  },

  earnedAmount: {
    value: 0,
    key: 'global-earned-amount',
  },

  availableAmount: {
    value: 0,
    key: 'global-available-amount',
  },

  completedBonusCount: {
    value: 0,
    key: 'global-completed-bonus-count',
  },

  // View preferences
  viewMode: {
    value: 'table', // 'table' or 'grid'
    key: 'global-view-mode',
  },

  sortBy: {
    value: 'amount',
    key: 'global-sort-by',
  },

  sortOrder: {
    value: 'desc',
    key: 'global-sort-order',
  },

  // UI state
  loading: {
    value: false,
    key: 'global-loading',
  },

  error: {
    value: null,
    key: 'global-error',
  },

  // Modal states
  editModalOpen: {
    value: false,
    key: 'global-edit-modal-open',
  },

  inviteModalOpen: {
    value: false,
    key: 'global-invite-modal-open',
  },

  // Theme
  darkMode: {
    value: false,
    key: 'global-dark-mode',
  },

  // Friends
  friends: {
    value: [],
    key: 'global-friends',
  },

  pendingRequests: {
    value: [],
    key: 'global-pending-requests',
  },

  incomingRequests: {
    value: [],
    key: 'global-incoming-requests',
  },

  // Pagination
  currentPage: {
    value: 0,
    key: 'global-current-page',
  },

  rowsPerPage: {
    value: 25,
    key: 'global-rows-per-page',
  },
};

/**
 * Helper function to reset all global state
 * Useful for logout or testing
 */
export const resetGlobalState = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    Object.keys(store).forEach(key => {
      localStorage.removeItem(`use-s-${store[key].key}`);
    });
  }
};

/**
 * Helper function to persist specific state to localStorage
 * @param {string} key - The store key to persist
 * @param {any} value - The value to persist
 */
export const persistState = (key, value) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      localStorage.setItem(`use-s-${key}`, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to persist state for key: ${key}`, error);
    }
  }
};

/**
 * Helper function to load persisted state from localStorage
 * @param {string} key - The store key to load
 * @param {any} defaultValue - The default value if not found
 * @returns {any} The persisted value or default
 */
export const loadPersistedState = (key, defaultValue) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const item = localStorage.getItem(`use-s-${key}`);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Failed to load persisted state for key: ${key}`, error);
      return defaultValue;
    }
  }
  return defaultValue;
};
